import { memo } from 'react'
import { SxProps } from '@mui/material'
import clsx from 'clsx'
import { Box, Container } from '@/components/Layout'
import { Text } from '@/components/Data'
import { useTranslations } from '@/core/translation'
import { useDomainInfo } from '@/utils'
import { Fanatics } from '@/assets/icons'
import { withErrorBoundary } from '@/components/Utils'
import GoogleReview from '../../Utils/GoogleReview'
import BannerSearch from './BannerSearch'
import BannerChips from './BannerChips'
import PartnersSlider from './PartnersSlider'
import BannerHeading from './BannerHeading'
import BannerBackgroundElements from './BannerBackgroundElements'
import BannerPoweredBy from './BannerPoweredBy'
import { bannerClasses } from './Banner.data'
import { bannerStyles as styles } from './Banner.style'
import { BannerProps } from './Banner.types'

function Banner({
  heading,
  subHeading,
  fullWidthHeading = false,
  hideHeading = false,
  hideSubHeading = false,
  smokyBlack = false,
  compact = false,
  fullSize = false,
  search = true,
  chips = false,
  review = false,
  partners = false,
  hideGradientImage = false,
  animated = false,
  isMainHeaderTransparent = false,
  noFullscreenSearch = false,
  sticky = false,
  headingProps = {},
  subHeadingProps = {},
  searchInputProps = {},
  contentWrapperProps = {},
  theme,
  ...props
}: BannerProps) {
  const t = useTranslations('banner')
  const { isFanaticsDomain } = useDomainInfo()

  const { sx: bannerSx, className = '', ...otherProps } = props
  const { sx: subHeadingSx, ...otherSubHeadingProps } = subHeadingProps
  const {
    sx: contentWrapperSx,
    className: contentWrapperClassName = '',
    ...otherContentWrapperProps
  } = contentWrapperProps

  return (
    <Box
      sx={[styles.banner, isFanaticsDomain && styles.bannerFanatics, bannerSx] as SxProps}
      className={clsx(
        {
          [bannerClasses.compact]: compact,
          [bannerClasses.partners]: partners,
          [bannerClasses.fullSize]: fullSize,
          [bannerClasses.mainHeaderTransparent]: isMainHeaderTransparent,
          [bannerClasses.sticky]: sticky,
          [bannerClasses.smokyBlack]: smokyBlack,
        },
        className,
      )}
      component={'section'}
      {...otherProps}>
      {/* Empty placeholder when there header is transparent to accommodate spacing */}
      {isMainHeaderTransparent && <Box sx={styles.transparentHeaderSpacer} />}

      <Container sx={styles.container} fixed>
        <Box
          sx={
            [
              styles.contentWrapper,
              isFanaticsDomain && styles.contentWrapperFanatics,
              contentWrapperSx,
            ] as SxProps
          }
          className={clsx(
            { [bannerClasses.fullWidthHeading]: fullWidthHeading },
            contentWrapperClassName,
          )}
          {...otherContentWrapperProps}>
          {isFanaticsDomain && !hideSubHeading && (
            <Box sx={styles.fanaticsIcon}>
              <Fanatics />
            </Box>
          )}
          {!hideHeading && <BannerHeading heading={heading} headingProps={headingProps} />}

          <Box sx={styles.subContentWrapper}>
            {!hideSubHeading && (
              <Text
                sx={
                  [
                    styles.subHeading,
                    isFanaticsDomain && styles.subHeadingFanatics,
                    subHeadingSx,
                  ] as SxProps
                }
                component={'h2'}
                {...otherSubHeadingProps}>
                {subHeading ?? t('subHeading', { isFanaticsDomain: String(isFanaticsDomain) })}
              </Text>
            )}

            {search && (
              <BannerSearch
                searchInputProps={searchInputProps}
                noFullscreenSearch={noFullscreenSearch}
              />
            )}

            {chips && <BannerChips />}

            {review && <GoogleReview />}

            {partners && <PartnersSlider />}
          </Box>

          {isFanaticsDomain && fullSize && <BannerPoweredBy inline />}
        </Box>
      </Container>

      {isFanaticsDomain && fullSize && <BannerPoweredBy />}

      {/* background elements */}
      <BannerBackgroundElements
        theme={theme}
        hideGradientImage={hideGradientImage}
        animated={animated}
      />
    </Box>
  )
}

export default memo(withErrorBoundary(Banner))
